import * as React from "react";
import {
  Table,
  Link,
  SpaceBetween,
  Header,
  CopyToClipboard,
  Button,
} from "@amzn/awsui-components-react";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import { FusePlan } from "@amzn/fuse-accounting-central-datastore-client";
import { AccountingRegistrationModal } from "./accounting-registration-modal";
import { SPLIT_PANEL_STICKY_HEADER_OFFSET } from "./split-panel.helper";

interface AssociatedPlansProps {}

export function AssociatedPlans(props: AssociatedPlansProps) {
  const { selectedArd, ardRegionMap } = useJemGen();
  const [showRegistrationModal, setShowRegistrationModal] =
    React.useState(false);
  const [selectedRegistrationId, setSelectedRegistrationId] = React.useState<
    string | null
  >(null);

  // Map region to appropriate domains
  const getDomainForRegion = (region: string) => {
    // Map regions to Fender domains (these are the same as the region values)
    const fenderDomainMap: { [key: string]: string } = {
      "devo-FE": "devo-FE",
      "prod-FE": "prod-FE",
      "prod-US": "prod-NA",
      "prod-EU": "prod-EU",
    };

    // Map regions to Bart domains
    const bartDomainMap: { [key: string]: string } = {
      "devo-FE": "beta",
      "prod-FE": "prod-jp",
      "prod-US": "prod-us",
      "prod-EU": "prod-eu",
    };

    // Map regions to ORCA Studio domains
    const orcaDomainMap: { [key: string]: string } = {
      "devo-FE": "beta.us-west-2",
      "prod-FE": "us-west-2",
      "prod-US": "us-east-1",
      "prod-EU": "eu-south-2",
    };

    return {
      fender: fenderDomainMap[region] || "devo-FE",
      bart: bartDomainMap[region] || "beta",
      orca: orcaDomainMap[region] || "beta.us-west-2",
    };
  };

  // Get region for the selected ARD
  const getRegionForArd = (ardId: string): string => {
    return ardRegionMap?.get(ardId) || "devo-FE";
  };

  // Generate external links for a plan
  const generateExternalLinks = (plan: FusePlan) => {
    if (!selectedArd?.id) return null;

    const region = getRegionForArd(selectedArd.id);
    const domains = getDomainForRegion(region);

    const fenderUrl = `https://fender.corp.amazon.com/domains/${domains.fender}/plans/${plan.id}`;
    const bartUrl = `https://${domains.bart}.bart-portal.fuse.amazon.dev/getplan/${plan.id}`;
    const orcaUrl = `https://${domains.orca}.studio.orca.amazon.dev/#/clients/fuse-billing/executions/actions/query?filters=Tags+%253D+${plan.id}`;

    return (
      <SpaceBetween direction="horizontal" size="s">
        <Link external href={fenderUrl}>
          Fender
        </Link>
        <Link external href={bartUrl}>
          Bart
        </Link>
        <Link external href={orcaUrl}>
          WF Samples
        </Link>
      </SpaceBetween>
    );
  };

  const handleRegistrationClick = (registrationId: string) => {
    setSelectedRegistrationId(registrationId);
    setShowRegistrationModal(true);
  };

  const handleModalDismiss = () => {
    setShowRegistrationModal(false);
    setSelectedRegistrationId(null);
  };

  const columnDefinitions = [
    {
      id: "id",
      header: "Plan ID",
      width: 350,
      cell: (plan: FusePlan) => {
        if (!plan.id) {
          return "N/A";
        }
        return (
          <CopyToClipboard
            copyButtonAriaLabel="Copy Plan ID"
            copyErrorText="Plan ID failed to copy"
            copySuccessText="Plan ID copied"
            textToCopy={plan.id}
            variant="inline"
          />
        );
      },
    },
    {
      id: "accountingRegistrationId",
      header: "Accounting Registration ID",
      width: 250,
      cell: (plan: FusePlan) => {
        if (!plan.accountingRegistrationId) {
          return "N/A";
        }
        return (
          <Button
            variant="inline-link"
            onClick={() =>
              handleRegistrationClick(plan.accountingRegistrationId!)
            }
            iconName="external"
            iconAlign="right"
          >
            {plan.accountingRegistrationId}
          </Button>
        );
      },
    },
    {
      id: "fusenEnabled",
      header: "Fusen Enabled",
      width: 100,
      cell: (plan: FusePlan) => (plan.fusenEnabled ? "True" : "False"),
    },
    {
      id: "migrationTargetStage",
      header: "Migration Target Stage",
      width: 120,
      cell: (plan: FusePlan) => plan.migrationTargetStage || "N/A",
    },
    {
      id: "glLineDescriptionPartnerName",
      header: "GL Line Description Partner Name",
      width: 350,
      cell: (plan: FusePlan) => plan.glLineDescriptionPartnerName || "N/A",
    },
    {
      id: "fusenEnableDate",
      header: "Fusen Enable Date",
      width: 180,
      cell: (plan: FusePlan) => plan.fusenEnableDate || "N/A",
    },
    {
      id: "externalLinks",
      header: "External Links",
      minWidth: 300,
      cell: (plan: FusePlan) => generateExternalLinks(plan),
    },
  ];

  const associatedPlans = selectedArd?.associatedPlans || [];

  if (!selectedArd) {
    return <>No ARD selected</>;
  }

  return (
    <>
      <Table
        columnDefinitions={columnDefinitions}
        items={associatedPlans}
        loadingText="Loading associated plans"
        sortingDisabled={true}
        stickyHeader
        stickyHeaderVerticalOffset={SPLIT_PANEL_STICKY_HEADER_OFFSET}
        stickyColumns={{ first: 1, last: 1 }}
        empty={
          <SpaceBetween size="m">
            <div>No associated plans found</div>
          </SpaceBetween>
        }
        header={
          <Header variant="h3" counter={`(${associatedPlans.length})`}>
            Associated Plans
          </Header>
        }
      />

      <AccountingRegistrationModal
        visible={showRegistrationModal}
        accountingRegistrationId={selectedRegistrationId}
        onDismiss={handleModalDismiss}
      />
    </>
  );
}
